import React from 'react';
import mediaQuery from 'css-mediaquery';
import { useTheme } from 'styled-components/macro';

const useEnhancedEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

const widthMap = {
  mobile: '0px',
  tablet: '768px',
  desktop: '1024px',
};

// https://github.com/mui-org/material-ui/blob/next/packages/material-ui/src/useMediaQuery/useMediaQuery.js
export default function useMediaQuery(queryInput: string) {
  const query = queryInput.replace(/^@media( ?)/m, '');
  const theme = useTheme();

  const supportMatchMedia =
    typeof window !== 'undefined' && typeof window.matchMedia !== 'undefined';

  const ssrMatchMedia = (query: string) => ({
    matches: mediaQuery.match(query, {
      // The estimated CSS width of the browser.
      width: theme.deviceType ? widthMap[theme.deviceType] : widthMap.desktop,
    }),
  });

  const matchMedia = supportMatchMedia ? window.matchMedia : null;

  const [match, setMatch] = React.useState(() => {
    if (matchMedia && supportMatchMedia) {
      return matchMedia(query).matches;
    }
    if (ssrMatchMedia) {
      return ssrMatchMedia(query).matches;
    }

    return false;
  });

  useEnhancedEffect(() => {
    let active = true;

    if (!supportMatchMedia || !matchMedia) {
      return undefined;
    }

    const queryList: MediaQueryList = matchMedia(query);
    const updateMatch = () => {
      // Workaround Safari wrong implementation of matchMedia
      // TODO can we remove it?
      // https://github.com/mui-org/material-ui/pull/17315#issuecomment-528286677
      if (active) {
        setMatch(queryList.matches);
      }
    };
    updateMatch();
    queryList.addEventListener('change', updateMatch);
    return () => {
      active = false;
      queryList.removeEventListener('change', updateMatch);
    };
  }, [query, matchMedia, supportMatchMedia]);

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useDebugValue({ query, match });
  }

  return match;
}
